import React, { useState, useRef, useEffect, useCallback } from 'react'
import { parsePriceInput, priceToMillions } from '../../utils/formatters'
import { useInlineEditorBehavior } from '../../hooks/useInlineEditorBehavior'

interface InlinePriceEditorProps {
  /** Current value in storage format (e.g., 37000000 for $37M) */
  value: number | null | undefined
  /** Field name for display/debugging purposes */
  fieldName: string
  /** Called when user saves a new value */
  onSave: (newValue: number | null) => Promise<void>
  /** Called when user cancels editing */
  onCancel: () => void
  /** Optional placeholder text */
  placeholder?: string
}

/**
 * Inline editor for price fields in market tables.
 * - Displays value in millions (e.g., "1.975" for $1.975M)
 * - Auto-saves on blur or Enter key
 * - Cancels on Escape key
 * - Handles decimal input up to 3 places
 */
export default function InlinePriceEditor({
  value,
  fieldName,
  onSave,
  onCancel,
  placeholder = '0.000'
}: InlinePriceEditorProps) {
  // Convert storage value to millions for editing
  const initialValue = priceToMillions(value)
  const [inputValue, setInputValue] = useState(
    initialValue !== null ? initialValue.toString() : ''
  )
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Focus and select input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [])

  const handleSave = useCallback(async () => {
    if (isSaving) return

    // Parse the input value (user enters millions, we store full number)
    const newValue = parsePriceInput(inputValue)

    // Check if value actually changed
    const oldValue = value ?? null

    if (newValue === oldValue) {
      onCancel()
      return
    }

    setIsSaving(true)
    try {
      await onSave(newValue)
    } catch (error) {
      // Stay in edit mode on error
      setIsSaving(false)
    }
  }, [inputValue, value, onSave, onCancel, fieldName, isSaving])

  const { handleKeyDown, handleBlur } = useInlineEditorBehavior({
    onSave: handleSave,
    onCancel,
    containerRef
  })

  return (
    <div ref={containerRef} className="absolute z-50 bg-white shadow-lg rounded p-1 -ml-1 -mt-1">
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        disabled={isSaving}
        placeholder={placeholder}
        className={`
          w-24 px-2 py-1 text-sm text-center
          border border-green-500 rounded
          focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent
          ${isSaving ? 'bg-gray-100 text-gray-500' : 'bg-white'}
        `}
      />
      {/* Visual indicator showing format hint */}
      <div className="absolute -bottom-4 left-1 text-[10px] text-gray-400">
        $M
      </div>
    </div>
  )
}

